import { SyntaxNode } from '@lezer/common';
import { parser } from './parser';
import * as terms from './parser.terms';

type Options = {
  maxCharactersPerLine: number;
  tabWidth: number;
};

const defaultOptions = { maxCharactersPerLine: 80, tabWidth: 2 };

const MATCHERS = [`=`, `=~`, `!=`, `!~`];

const QUOTE_CHARACTERS = ["'", `"`, '`'];

// Pretty print the provided PromQL query. Note: will throw on invalid query,
// or a query it doesn't understand.
export function prettify(query: string, opts: Partial<Options> = {}): string {
  const options: Options = {
    ...defaultOptions,
    ...opts,
  };
  const tree = parser.configure({ strict: true }).parse(query);

  function raw(node: SyntaxNode | null): string {
    if (!node) return '';
    return query.slice(node.from, node.to);
  }

  function lowerCased(node: SyntaxNode | null): string {
    return raw(node).toLowerCase();
  }

  function fits(line: string): boolean {
    return line.length <= options.maxCharactersPerLine;
  }

  function indent(level: number): string {
    return ' '.repeat(level * options.tabWidth);
  }

  function indentedCompact(level: number, current: SyntaxNode): string {
    return `${indent(level)}${compact(current)}`;
  }

  // isUTF8NameMatcher returns whether the matcher expression may be an utf-8 metric name matcher.
  // The detection is best-effort, as ideally the sort would be performed on the AST nodes, and not
  // the matcher strings. However, this should be good enough for 99% of cases.
  function isUTF8NameMatcher(matcherStr: string): boolean {
    for (const quoteSymbol of QUOTE_CHARACTERS) {
      if (!matcherStr.startsWith(quoteSymbol) || !matcherStr.endsWith(quoteSymbol)) {
        continue;
      }
      // Quoted string, now let's make sure the value inside doesn't look like a standard label matcher.
      for (const op of MATCHERS) {
        // If the string starts with a quote, e.g. `"`, check if it also contains either `!="` or `"=!`.
        if (matcherStr.includes(op.concat(quoteSymbol)) || matcherStr.includes(quoteSymbol.concat(op))) {
          return false;
        }
      }
      return true;
    }

    return false;
  }

  function compact(node: SyntaxNode | null | undefined): string {
    if (!node) return '';
    switch (node.type.id) {
      case terms.MatrixSelector:
        const { expr, range } = parseMatrix(node);
        return `${compact(expr)}[${compact(range)}]`;
      case terms.VectorSelector: {
        const { name, matchers } = parseVector(node);
        const res = `${compact(name)}${compact(matchers)}`;
        return res == '' ? '{}' : res;
      }
      case terms.LabelMatchers: {
        const matchers = children(node);

        if (matchers.length == 0) {
          return '';
        }
        return `{${matchers
          .map(compact)
          .sort((a, b) => {
            // only one of these can be true in a valid expression
            const isUTF8NameMatcherA = isUTF8NameMatcher(a);
            const isUTF8NameMatcherB = isUTF8NameMatcher(b);
            // make sure quoted name matchers always come first
            if (isUTF8NameMatcherA && !isUTF8NameMatcherB) {
              return -1;
            } else if (!isUTF8NameMatcherA && isUTF8NameMatcherB) {
              return 1;
            }
            // Naively attempt to do lexicographical sorting by stripping the first character,
            // if it is a quote.
            let unprefixedA = a;
            let unprefixedB = b;
            for (const quoteSymbol of QUOTE_CHARACTERS) {
              if (a.startsWith(quoteSymbol)) {
                unprefixedA = a.substring(1);
              }
              if (b.startsWith(quoteSymbol)) {
                unprefixedB = b.substring(1);
              }
            }
            if (unprefixedA < unprefixedB) {
              return -1;
            } else if (unprefixedA > unprefixedB) {
              return 1;
            }
            return 0;
          })
          .join(',')}}`;
      }
      case terms.LabelMatcher: {
        return children(node).map(compact).join('');
      }
      case terms.StepInvariantExpr: {
        const { expr, at } = parseStepInvariantExpr(node);
        return `${compact(expr)} @ ${compact(at)}`;
      }
      case terms.AtModifierPreprocessors: {
        // First child will be either start or end
        return `${compact(node.firstChild)}()`;
      }
      case terms.OffsetExpr: {
        const { expr, isSub, offset } = parseOffsetExpr(node);
        return `${compact(expr)} offset ${isSub ? '-' : ''}${compact(offset)}`;
      }
      case terms.SubqueryExpr: {
        const { expr, range, step } = parseSubqueryExpr(node);
        return `${compact(expr)}[${compact(range)}:${step ? compact(step) : ''}]`;
      }
      case terms.MatchingModifierClause: {
        const compactChildren = children(node).map(compact);
        switch (compactChildren.length) {
          // insert () for empty arguments to group_left/group_right
          case 3:
            return `${compactChildren.join(' ')} ()`;
          case 2:
          case 4:
            return compactChildren.join(' ');
          default:
            raw(node);
        }
      }
      case terms.GroupingLabels: {
        return `(${children(node).map(compact).join(', ')})`;
      }
      case terms.BinaryExpr: {
        const { op, hasBool, hasOuter, modifier, lhs, rhs } = parseBinaryExpr(node);
        let operatorPart = compact(op);
        if (hasBool) {
          operatorPart += ' bool';
        }
        if (hasOuter) {
          operatorPart += ' __outer';
        }
        if (modifier) {
          operatorPart += ` ${compact(modifier)}`;
        }
        return `${compact(lhs)} ${operatorPart} ${compact(rhs)}`;
      }
      case terms.AggregateExpr: {
        const { op, modifier, body } = parseAggregateExpr(node);
        let opPart = compact(op);
        const modifierPart = compact(modifier);
        if (modifierPart.length > 0) {
          opPart += ` ${modifierPart} `;
        }
        return `${opPart}(${compact(body)})`;
      }
      case terms.AggregateModifier: {
        const kind = mustFirstChild(node);
        const grouping = compact(node.lastChild);
        if (kind.type.id === terms.By && grouping == '()') {
          return '';
        }
        return `${compact(kind)} ${grouping}`;
      }
      case terms.FunctionCall: {
        const { name, args } = parseFunctionCall(node);
        return `${compact(name)}(${compact(args)})`;
      }
      case terms.FunctionCallBody: {
        return children(node).map(compact).join(', ');
      }
      case terms.NumberLiteral: {
        switch (lowerCased(node).replaceAll(' ', '')) {
          case '+inf':
          case 'inf':
            return '+Inf';
          case '-inf':
            return '-Inf';
          case 'nan':
            return 'NaN';
          default:
            return raw(node);
        }
      }
      case terms.AggregateOp:
      case terms.Start:
      case terms.End:
      case terms.By:
      case terms.Without:
      case terms.GroupLeft:
      case terms.GroupRight:
      case terms.Ignoring:
      case terms.On:
      case terms.Or:
      case terms.Unless:
      case terms.And:
      case terms.Atan2:
        return lowerCased(node);
      default:
        return raw(node);
    }
  }

  function pretty(node: SyntaxNode, level: number): string {
    switch (node.type.id) {
      case terms.PromQL: {
        const expr = findFirstNonCommentChild(node);
        if (expr == null || containsComments(expr)) {
          return query;
        }
        // also drop trailing spaces, so that indentation does not stack
        const pre = emptyIfWhitespace(query.slice(0, expr.from)).replace(/ *$/, '');
        const post = emptyIfWhitespace(query.slice(expr.to));
        return `${pre}${pretty(expr, level)}${post}`;
      }
      case terms.BinaryExpr: {
        const candidate = compact(node);
        if (fits(candidate)) {
          return `${indent(level)}${candidate}`;
        }
        const { op, hasBool, hasOuter, modifier, lhs, rhs } = parseBinaryExpr(node);
        let operatorPart = compact(op);
        if (hasBool) {
          operatorPart += ' bool';
        }
        if (hasOuter) {
          operatorPart += ' __outer';
        }
        if (modifier) {
          operatorPart += ` ${compact(modifier)}`;
        }
        return `${pretty(lhs, level + 1)}\n${indent(level)}${operatorPart}\n${pretty(rhs, level + 1)}`;
      }
      case terms.ParenExpr: {
        const candidate = compact(node);
        if (fits(candidate)) {
          return `${indent(level)}${candidate}`;
        }
        return `${indent(level)}(\n${pretty(mustFirstChild(node), level + 1)}\n${indent(level)})`;
      }
      case terms.FunctionCall: {
        const candidate = compact(node);
        if (fits(candidate)) {
          return `${indent(level)}${candidate}`;
        }
        const { name, args } = parseFunctionCall(node);
        return `${indent(level)}${compact(name)}(\n${pretty(args, level + 1)}\n${indent(level)})`;
      }
      case terms.FunctionCallBody: {
        return children(node)
          .map((child) => pretty(child, level))
          .join(',\n');
      }
      case terms.UnaryExpr: {
        const { op, expr } = parseUnaryExpr(node);
        // Don't want to double indent the first line, so let's trim it. We keep the indent level,
        // so that deeper children are indented.
        return `${indent(level)}${compact(op)}${pretty(expr, level).trimStart()}`;
      }
      case terms.AggregateExpr: {
        const candidate = compact(node);
        if (fits(candidate)) {
          return `${indent(level)}${candidate}`;
        }
        const { op, modifier, body } = parseAggregateExpr(node);
        let opPart = compact(op);
        const modifierPart = compact(modifier);
        if (modifierPart.length > 0) {
          opPart += ` ${modifierPart} `;
        }
        return `${indent(level)}${opPart}(\n${pretty(body, level + 1)}\n${indent(level)})`;
      }
      case terms.SubqueryExpr: {
        const candidate = compact(node);
        if (fits(candidate)) {
          return `${indent(level)}${candidate}`;
        }
        const { expr, range, step } = parseSubqueryExpr(node);
        return `${pretty(expr, level)}[${compact(range)}:${compact(step)}]`;
      }
      case terms.OffsetExpr: {
        const { expr, isSub, offset } = parseOffsetExpr(node);
        return `${pretty(expr, level)} offset ${isSub ? '-' : ''}${compact(offset)}`;
      }
      case terms.StepInvariantExpr: {
        const { expr, at } = parseStepInvariantExpr(node);
        return `${pretty(expr, level)} @ ${compact(at)}`;
      }
      default:
        return indentedCompact(level, node);
    }
  }

  return pretty(tree.topNode, 0);
}

function mustFirstChild(node: SyntaxNode): SyntaxNode {
  const next = node.firstChild;
  if (!next) {
    throw new Error(`Expected a ${node.type.name} node to have a first child`);
  }
  return next;
}

function mustLastChild(node: SyntaxNode): SyntaxNode {
  const next = node.lastChild;
  if (!next) {
    throw new Error(`Expected a ${node.type.name} node to have a last child`);
  }
  return next;
}

function mustNextSibling(node: SyntaxNode): SyntaxNode {
  const next = node.nextSibling;
  if (!next) {
    throw new Error(`Expected a ${node.type.name} node to have a next sibling`);
  }
  return next;
}

function findFirstNonCommentChild(node: SyntaxNode): SyntaxNode | null {
  for (let child = node.firstChild; child; child = child.nextSibling) {
    if (child.type.id !== terms.LineComment) {
      return child;
    }
  }
  return null;
}

function containsComments(expr: SyntaxNode): boolean {
  if (expr.type.id === terms.LineComment) {
    return true;
  }
  for (let child = expr.firstChild; child; child = child?.nextSibling) {
    if (containsComments(child)) {
      return true;
    }
  }
  return false;
}

function forEachChild(node: SyntaxNode, f: (node: SyntaxNode) => void) {
  for (let c = node.firstChild; c; c = c.nextSibling) f(c);
}

function children(node: SyntaxNode): SyntaxNode[] {
  const res: SyntaxNode[] = [];
  forEachChild(node, (child) => res.push(child));
  return res;
}

function parseBinaryExpr(node: SyntaxNode) {
  const lhs = mustFirstChild(node);
  const rhs = mustLastChild(node);
  const op = mustNextSibling(lhs);
  const hasBool = !!node.getChild(terms.BoolModifier);
  const hasOuter = !!node.getChild(terms.OuterModifier);
  const modifier = node.getChild(terms.MatchingModifierClause);
  return {
    lhs,
    rhs,
    op,
    hasBool,
    hasOuter,
    modifier,
  };
}

function parseAggregateExpr(node: SyntaxNode) {
  const op = mustFirstChild(node);
  const modifier = node.getChild(terms.AggregateModifier);
  const body = node.getChild(terms.FunctionCallBody)!;
  return {
    op,
    modifier,
    body,
  };
}

function emptyIfWhitespace(s: string) {
  return s.trim() === '' ? '' : s;
}

function parseOffsetExpr(node: SyntaxNode) {
  const expr = mustFirstChild(node);
  const offsetKeyword = mustNextSibling(expr);
  const isSub = offsetKeyword.nextSibling?.type.id === terms.Sub;
  const offset = mustLastChild(node);
  return {
    expr,
    isSub,
    offset,
  };
}

function parseSubqueryExpr(node: SyntaxNode) {
  const expr = mustFirstChild(node);
  const range = mustNextSibling(expr);
  const step = range.nextSibling;
  return {
    expr,
    range,
    step,
  };
}

function parseVector(node: SyntaxNode) {
  if (mustFirstChild(node).type.id === terms.LabelMatchers) {
    // Only matchers variant: `{foo="bar"}`
    return {
      matchers: mustFirstChild(node),
    };
  } else {
    // Regular variant: `metric{foo="bar"}` or `metric`
    return {
      name: mustFirstChild(node),
      matchers: mustFirstChild(node).nextSibling,
    };
  }
}

function parseStepInvariantExpr(node: SyntaxNode) {
  const expr = mustFirstChild(node);
  const at = mustLastChild(node);
  return {
    expr,
    at,
  };
}

function parseMatrix(node: SyntaxNode) {
  const expr = mustFirstChild(node);
  const range = mustNextSibling(expr);
  return {
    expr,
    range,
  };
}

function parseFunctionCall(node: SyntaxNode) {
  const name = mustFirstChild(node);
  const args = mustLastChild(node);
  return {
    name,
    args,
  };
}

function parseUnaryExpr(node: SyntaxNode) {
  const op = mustFirstChild(node);
  const expr = mustLastChild(node);
  return {
    op,
    expr,
  };
}
